"use client"

import clsx from "clsx"
import Image from "next/image"
import React, { useState, useMemo, type JSX } from "react"
import ReactPlayer from "react-player/lazy"

import { WistiaItem } from "@/types/video"

import PlayBtnIconWithBG from "../../../public/icons/play-button.svg"

import WistiaPlayer from "./WistiaLocalPlayer"
import WistiaPopover from "./WistiaPopoverPlayer"

/**
 * Functional component for Video Player.
 *
 * @component
 * @param videoURL - url of video embed, can be YouTube or Vimeo
 * @param title - title tag on iFrame
 * @param disabled - disable the video from playing and don't render in dom
 * @param image - placeholder image
 * @param staticImagePlaceholder - placeholder image which uses StaticImage from gatsby-plugin-image
 * @param className - optional classes for wrapper div around iFrame
 * @param isCard - optional card styling
 * @param bgColor - optional background color class
 */

interface VideoPlayerProps {
  videoURL?: string
  title?: any | undefined
  disabled?: boolean
  image?: string | React.ReactNode | Element | any
  staticImagePlaceholder?: JSX.Element
  className?: string
  isCard?: boolean
  bgColor?: string
  wistiaVideo?: WistiaItem
  popover?: boolean
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoURL,
  title,
  disabled = false,
  image,
  staticImagePlaceholder,
  className,
  isCard = false,
  // The bg color class of black shows on the videos with a light page background.
  // Not sure why the class is even on here but I made it a prop so as not to break anything.
  // On the CBDC video I'll pass bg-transparent in the prop to avoid the black pixelated edges.
  bgColor = "bg-black-100",
  wistiaVideo,
  popover = false,
}) => {
  const [playing, setPlaying] = useState(false)
  const handlePlayAction = () => {
    setPlaying(true)
  }

  // Process videoURL to ensure YouTube URLs are properly formatted for embedding
  const formattedVideoURL = useMemo(() => {
    if (!videoURL) return ""

    // If it's already an embed URL, add autoplay parameter if not present
    if (videoURL.includes("/embed/")) {
      // Add autoplay parameter if needed
      return videoURL.includes("autoplay=")
        ? videoURL
        : `${videoURL}${videoURL.includes("?") ? "&" : "?"}autoplay=1`
    }

    // Handle youtube.com URLs
    if (videoURL.includes("youtube.com/watch")) {
      try {
        const url = new URL(videoURL)
        const videoId = url.searchParams.get("v")
        if (videoId)
          return `https://www.youtube.com/embed/${videoId}?autoplay=1`
      } catch (error) {
        console.error("Invalid YouTube URL:", error)
        // Return original URL if parsing fails
        return videoURL
      }
    }

    // Handle youtu.be URLs
    if (videoURL.includes("youtu.be/")) {
      try {
        const url = new URL(videoURL)
        // Extract video ID from pathname (remove leading slash)
        const videoId = url.pathname.substring(1)
        return `https://www.youtube.com/embed/${videoId}?autoplay=1`
      } catch (error) {
        console.error("Invalid YouTube short URL:", error)
        // Fallback to string splitting if URL parsing fails
        try {
          const videoId = videoURL.split("youtu.be/")[1].split("?")[0]
          return `https://www.youtube.com/embed/${videoId}?autoplay=1`
        } catch (splitError) {
          console.error(
            "Failed to parse YouTube ID using string split:",
            splitError,
          )
          return videoURL
        }
      }
    }

    // Return original URL if it doesn't match known patterns
    return videoURL
  }, [videoURL])

  if (
    wistiaVideo &&
    popover &&
    wistiaVideo.items &&
    wistiaVideo.items.length > 0
  ) {
    return <WistiaPopover videoId={wistiaVideo.items[0].hashed_id} />
  }

  if (wistiaVideo && wistiaVideo.items && wistiaVideo.items.length > 0) {
    return (
      <WistiaPlayer
        videoId={wistiaVideo.items[0].hashed_id}
        title={title}
        className={className}
      />
    )
  }

  // Check if the URL is a Vimeo URL
  const isVimeo = videoURL && videoURL.includes("vimeo.com")
  const isYoutube =
    videoURL &&
    (videoURL.includes("youtube.com") || videoURL.includes("youtu.be"))

  if (playing && !disabled) {
    if (isVimeo || isYoutube) {
      return (
        <div className={clsx("rounded-lg", bgColor)}>
          <div
            className={clsx(
              className,
              "relative overflow-hidden aspect-video rounded-lg",
            )}
          >
            <ReactPlayer
              url={isYoutube ? formattedVideoURL : videoURL}
              playing={playing}
              controls
              width="100%"
              height="100%"
              className="react-player"
              config={{
                vimeo: {
                  playerOptions: {
                    quality: "4K",
                  },
                },
                youtube: {
                  playerVars: {
                    vq: "highres",
                  },
                },
              }}
            />
          </div>
        </div>
      )
    }
    return (
      <div className={clsx("rounded-lg", bgColor)}>
        <div
          className={clsx(
            className,
            "relative overflow-hidden aspect-video rounded-lg",
          )}
        >
          <iframe
            src={formattedVideoURL}
            width="100%"
            height="100%"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            title={title ?? "Video"}
            frameBorder="0"
            allowFullScreen
          />
        </div>
      </div>
    )
  }

  return (
    <div className={clsx("rounded-lg", bgColor)}>
      <div
        className="relative overflow-hidden rounded-lg group aspect-video"
        onClick={() => {
          handlePlayAction()
        }}
        onKeyDown={() => {
          handlePlayAction()
        }}
        tabIndex={0}
        role="button"
      >
        {/* You can pass a StaticImage, ie from gatsby-plugin-image
        You can pass an img path as a string or you can pass the gatsbyImageData from a GraphQL query */}
        {staticImagePlaceholder !== undefined ? (
          staticImagePlaceholder
        ) : (
          <Image
            src={image}
            className="group-hover:opacity-80"
            alt={title || "Video Cover"}
            layout="fill"
          />
        )}

        {/* if its a card then only show the small hover button arrow. Otherwise size button arrow based on size */}
        <div className="absolute inset-0 flex items-center justify-center w-full h-full overflow-hidden bg-cover rounded cursor-pointer">
          <div
            className={clsx(
              "relative w-20 h-20 transition-all rounded-full backdrop-blur-md group-hover:scale-110",
              !isCard && "md:h-32 md:w-32 lg:h-30 lg:w-30",
            )}
          >
            <PlayBtnIconWithBG
              className={clsx(
                "w-20 h-20",
                !isCard && "md:h-32 md:w-32 lg:h-30 lg:w-30",
              )}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default VideoPlayer
